/* ================================================================
   Ähnlichkeit von Regionen, Winzern und Weinen — EINE Regel für
   Server (Admin-Prüfung) und Client (Dublettenfrage des Nutzers).

   Bis Oktober 2026 verglichen beide Seiten nur den Namen, als
   Teilzeichenkette: „Riesling“ passte auf jeden Wein mit „Riesling“
   im Namen, gleich von welchem Winzer. An einem gebauten Bestand
   (400 Winzer, 1806 Weine, 200 Vorschläge) waren das im Schnitt 114
   Treffer je Vorschlag, und jeder neue Wein wurde beim Nutzer als
   mögliche Dublette zurückgehalten.

   Jetzt:
     - Namen werden wortweise verglichen, nicht als Teilzeichenkette.
     - Prädikate und Ausbaustufen müssen übereinstimmen (Kabinett ≠
       Spätlese, trocken ≠ feinherb, Smaragd ≠ Federspiel).
     - Haben beide Namen ein eigenes unterscheidendes Wort (Lage,
       Cuvée), sind es zwei Weine (Goldtröpfchen ≠ Heiligenstein).
     - Kontext schließt aus, wenn BEIDE Seiten die Angabe haben:
       anderer Winzer, anderer Typ, anderes Land, keine gemeinsame
       Rebsorte. Fehlt eine Angabe, schließt sie nichts aus.
     - Zwei Stufen: „sehr“ (wahrscheinlich dieselbe) und „moeglich“,
       mit Merkmalen, die die Oberfläche als Begründung zeigt.

   CommonJS, damit server.js sie direkt laden kann; esbuild bündelt
   sie für den Client mit. Keine Abhängigkeiten.
   ================================================================ */

/* Betriebsbezeichnungen und Füllwörter — tragen nichts zur Identität bei. */
const FUELLWOERTER = new Set([
  "weingut", "weinhaus", "winzerhof", "weinbau", "chateau", "domaine", "dom", "bodega", "bodegas",
  "tenuta", "cantina", "azienda", "agricola", "maison", "estate", "winery", "gut",
  "de", "du", "des", "la", "le", "les", "di", "del", "della", "von", "vom", "zu", "and", "und", "the",
]);

/* Prädikate, Ausbau- und Qualitätsstufen. Verschieden → verschiedene Weine. */
const STUFEN = new Set([
  "kabinett", "spatlese", "auslese", "beerenauslese", "trockenbeerenauslese", "eiswein", "ba", "tba",
  "federspiel", "smaragd", "steinfeder", "gg", "erstelage", "grosselage", "gutswein", "ortswein",
  "trocken", "feinherb", "halbtrocken", "lieblich", "edelsuss", "brut", "extrabrut", "nature", "sec", "demisec",
  "reserve", "reserva", "riserva", "granreserva", "crianza", "superiore", "classico",
  "premiercru", "grandcru", "spatlesetrocken", "vendangestardives", "sgn",
]);

/* Rebsorten und ihre Wortteile — sie unterscheiden keine Weine
   desselben Winzers voneinander, eine Lage schon. */
const REBEN = new Set([
  "riesling", "gutsriesling", "spatburgunder", "fruhburgunder", "grauburgunder", "weissburgunder", "pinot", "noir",
  "blanc", "gris", "grigio", "meunier", "silvaner", "sylvaner", "muller", "thurgau", "rivaner", "gruner",
  "veltliner", "blaufrankisch", "zweigelt", "lemberger", "trollinger", "dornfelder", "scheurebe", "kerner",
  "gewurztraminer", "traminer", "muskateller", "muscat", "moscato", "chardonnay", "sauvignon", "cabernet",
  "franc", "merlot", "syrah", "shiraz", "grenache", "garnacha", "tempranillo", "nebbiolo", "barbera",
  "sangiovese", "dolcetto", "gamay", "malbec", "zinfandel", "primitivo", "chenin", "viognier", "semillon",
  "albarino", "verdejo", "furmint", "rose", "rosso", "bianco", "rouge", "blanc", "rot", "weiss", "cuvee",
]);

/* Mehrwortbegriffe vor dem Zerlegen zusammenziehen, Umschriften angleichen. */
const PHRASEN = [
  [/\bgrosses gewachs\b/g, "gg"], [/\berste lage\b/g, "erstelage"], [/\bgrosse lage\b/g, "grosselage"],
  [/\bextra brut\b/g, "extrabrut"], [/\bdemi sec\b/g, "demisec"], [/\bgran reserva\b/g, "granreserva"],
  [/\bpremier cru\b/g, "premiercru"], [/\b1er cru\b/g, "premiercru"], [/\bgrand cru\b/g, "grandcru"],
  [/\bvendanges tardives\b/g, "vendangestardives"], [/\bspaetlese\b/g, "spatlese"], [/\bgruener\b/g, "gruner"],
  [/\bspaetburgunder\b/g, "spatburgunder"], [/\bweisser\b/g, "weiss"], [/\bedelsuess\b/g, "edelsuss"],
  [/\bmueller\b/g, "muller"], [/\bdr\b\.?/g, "dr"],
];

function grundform(s) {
  let x = String(s || "").toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ").trim();
  for (const [re, ersatz] of PHRASEN) x = x.replace(re, ersatz);
  return x;
}

const woerter = (s) => grundform(s).split(" ").filter((w) => w && !FUELLWOERTER.has(w));

/* Kompakter Schlüssel — fängt Schreibweisen ohne Leerzeichen ab
   („Scharzhof berger“). Entspricht dem früheren Namensschlüssel. */
const kompakt = (s) => woerter(s).join("");

/** Wie nah sind sich zwei Namen? { naehe: 0..1, lageFehlt } —
    naehe 0 heißt: sicher verschieden. */
function namensNaehe(a, b) {
  return naeheAus(woerter(a), kompakt(a), woerter(b), kompakt(b));
}

/* Kern des Namensvergleichs auf vorbereiteten Wörtern — die Zerlegung
   kostet mehr als der Vergleich und läuft deshalb nur einmal je Objekt. */
function naeheAus(wa, ka, wb, kb) {
  if (wa.length === 0 || wb.length === 0) return { naehe: 0, lageFehlt: false };
  const sa = new Set(wa), sb = new Set(wb);

  for (const w of wa) if (STUFEN.has(w) && !sb.has(w)) return { naehe: 0, lageFehlt: false };
  for (const w of wb) if (STUFEN.has(w) && !sa.has(w)) return { naehe: 0, lageFehlt: false };

  if (ka === kb) return { naehe: 1, lageFehlt: false };

  let nurA = 0, nurB = 0;
  for (const w of sa) if (!REBEN.has(w) && !STUFEN.has(w) && !sb.has(w)) nurA++;
  for (const w of sb) if (!REBEN.has(w) && !STUFEN.has(w) && !sa.has(w)) nurB++;
  if (nurA > 0 && nurB > 0) return { naehe: 0, lageFehlt: false };

  /* Gleiche unterscheidende Wörter, Rebsorte nur bei einem genannt:
     „Rausch“ und „Rausch Riesling“ sind sehr wahrscheinlich derselbe
     Wein. Nennen beide verschiedene Rebsorten, sind es zwei. */
  const kennA = wa.filter((w) => !REBEN.has(w) && !STUFEN.has(w));
  if (nurA === 0 && nurB === 0 && kennA.length > 0) {
    const rebA = wa.filter((w) => REBEN.has(w)), rebB = wb.filter((w) => REBEN.has(w));
    if (rebA.length && rebB.length && !rebA.some((w) => sb.has(w))) return { naehe: 0, lageFehlt: false };
    return { naehe: 0.8, lageFehlt: false };
  }

  let gemein = 0;
  for (const w of sa) if (sb.has(w)) gemein++;
  return { naehe: gemein / Math.max(sa.size, sb.size), lageFehlt: nurA + nurB > 0 };
}

/* ── Merkmale eines Objekts aus seiner Quelle ────────────────────
   Quelle ist ein Kellerbuch (`regions`) oder der Katalog
   (`regionen`); beide haben `winzer`. */
const regionenVon = (q) => (q && (q.regions || q.regionen)) || [];

function nachIdKarte(liste) {
  const m = new Map();
  for (const o of liste || []) if (o && o.id) m.set(o.id, o);
  return m;
}

/** Nachschlagehilfe für eine Quelle — einmal bauen, oft benutzen. */
function quelleVon(q) {
  return { regionen: nachIdKarte(regionenVon(q)), winzer: nachIdKarte(q && q.winzer) };
}

const ortWoerter = (s) => new Set(woerter(s));

function merkmale(typ, o, nachschlag) {
  const ns = nachschlag || { regionen: new Map(), winzer: new Map() };
  const name = (o && o.name) || "";
  const liste = woerter(name);
  const m = { typ, id: o && o.id, name, kompakt: liste.join(""), wortListe: liste, woerter: new Set(liste) };
  if (typ === "region") {
    m.land = (o && o.land) || null;
    return m;
  }
  if (typ === "winzer") {
    const r = o && o.regionId ? ns.regionen.get(o.regionId) : null;
    m.regionKey = r ? kompakt(r.name) : null;
    m.land = (r && r.land) || (o && o.land) || null;
    m.ort = o && o.ort ? ortWoerter(o.ort) : null;
    return m;
  }
  const wz = o && o.winzerId ? ns.winzer.get(o.winzerId) : null;
  const r = (o && o.regionId && ns.regionen.get(o.regionId)) || (wz && wz.regionId && ns.regionen.get(wz.regionId)) || null;
  m.winzerId = (o && o.winzerId) || null;
  m.winzerKey = wz ? kompakt(wz.name) : null;
  m.regionKey = r ? kompakt(r.name) : null;
  m.land = (r && r.land) || (o && o.land) || null;
  m.weintyp = (o && o.typ) || null;
  const reben = Array.isArray(o && o.rebsortenListe) ? o.rebsortenListe.map((x) => kompakt(x && x.name)).filter(Boolean) : [];
  m.reben = reben.length ? new Set(reben) : null;
  return m;
}

const SCHWELLE = 0.6;

/** Vergleich zweier Merkmalssätze desselben Typs.
    null — kein Treffer; sonst { stufe: "sehr"|"moeglich", punkte, merkmale: [Schlüssel] }.
    Die Merkmalsschlüssel sind deutsche Texte (t()-Schlüssel der Oberfläche). */
function vergleiche(typ, a, b) {
  /* Erst die billigen Ausschlüsse über den Kontext, dann der Name. */
  if (a.land && b.land && a.land !== b.land) return null;
  if (typ === "wein") {
    if (a.weintyp && b.weintyp && a.weintyp !== b.weintyp) return null;
    if (a.winzerId && b.winzerId && a.winzerId !== b.winzerId && !(a.winzerKey && a.winzerKey === b.winzerKey)) return null;
  }
  if (typ === "winzer" && a.regionKey && b.regionKey && a.regionKey !== b.regionKey) return null;

  const n = naeheAus(a.wortListe || woerter(a.name), a.kompakt != null ? a.kompakt : kompakt(a.name),
    b.wortListe || woerter(b.name), b.kompakt != null ? b.kompakt : kompakt(b.name));
  if (n.naehe < SCHWELLE) return null;
  const gruende = [n.naehe === 1 ? "Name gleich" : "Name ähnlich"];
  if (n.lageFehlt) gruende.push("Lage nur bei einem");
  let punkte = n.naehe;

  if (a.land && b.land && a.land !== b.land) return null;

  if (typ === "region") {
    if (a.land && b.land) { gruende.push("Land gleich"); punkte += 0.5; }
    return { stufe: n.naehe === 1 ? "sehr" : "moeglich", punkte, merkmale: gruende };
  }

  if (typ === "winzer") {
    if (a.regionKey && b.regionKey && a.regionKey !== b.regionKey) return null;
    if (a.ort && b.ort && a.ort.size && b.ort.size && ![...a.ort].some((w) => b.ort.has(w))) return null;
    const gleicheRegion = !!a.regionKey && a.regionKey === b.regionKey;
    const gleicherOrt = !!(a.ort && b.ort && a.ort.size && b.ort.size);
    if (gleicheRegion) { gruende.push("Region gleich"); punkte += 0.5; }
    if (gleicherOrt) { gruende.push("Ort gleich"); punkte += 0.5; }
    /* Familiennamen wiederholen sich — „sehr“ nur mit Ortsbezug. */
    return { stufe: n.naehe === 1 && (gleicheRegion || gleicherOrt) ? "sehr" : "moeglich", punkte, merkmale: gruende };
  }

  /* Wein */
  if (a.weintyp && b.weintyp && a.weintyp !== b.weintyp) return null;
  if (a.reben && b.reben && ![...a.reben].some((r) => b.reben.has(r))) return null;
  let gleicherWinzer = false;
  if (a.winzerId && b.winzerId) {
    gleicherWinzer = a.winzerId === b.winzerId || (!!a.winzerKey && a.winzerKey === b.winzerKey);
    if (!gleicherWinzer) return null;
  }
  if (gleicherWinzer) { gruende.push("gleicher Winzer"); punkte += 1; } else if (a.winzerId || b.winzerId) gruende.push("Winzer nur bei einem");
  if (a.weintyp && b.weintyp) { gruende.push("Typ gleich"); punkte += 0.2; }
  if (a.regionKey && b.regionKey) {
    if (a.regionKey === b.regionKey) { gruende.push("Region gleich"); punkte += 0.3; } else gruende.push("andere Region");
  }
  const sehr = n.naehe >= 0.8 && gleicherWinzer;
  return { stufe: sehr ? "sehr" : "moeglich", punkte, merkmale: gruende };
}

/* ── Index über eine Liste (Katalog oder Kellerbuch) ─────────────
   Wort → Einträge, damit nicht jeder mit jedem verglichen wird. */
const LISTE_IN = {
  region: (q) => regionenVon(q),
  winzer: (q) => (q && q.winzer) || [],
  wein: (q) => (q && q.weine) || [],
};

function baueIndex(typ, quelle) {
  const ns = quelleVon(quelle);
  const eintraege = [];
  const nachWort = new Map();
  const nachKompakt = new Map();
  for (const o of LISTE_IN[typ](quelle)) {
    if (!o || !o.id) continue;
    const e = { o, m: merkmale(typ, o, ns) };
    eintraege.push(e);
    for (const w of e.m.woerter) {
      if (!nachWort.has(w)) nachWort.set(w, []);
      nachWort.get(w).push(e);
    }
    if (e.m.kompakt) {
      if (!nachKompakt.has(e.m.kompakt)) nachKompakt.set(e.m.kompakt, []);
      nachKompakt.get(e.m.kompakt).push(e);
    }
  }
  return { typ, eintraege, nachWort, nachKompakt };
}

/** Ähnliche Einträge zu `m` (Merkmale) im Index — beste zuerst.
    Liefert [{ objekt, stufe, punkte, merkmale }]. */
function suche(index, m, { ausserId = null, grenze = Infinity } = {}) {
  const kandidaten = new Set(index.nachKompakt.get(m.kompakt) || []);
  for (const w of m.woerter) {
    /* Rebsorten und Stufen allein sind kein Anlass zum Vergleich —
       sonst würde wieder jeder „Riesling“ mit jedem verglichen. Die
       Treffer kommen über die übrigen Wörter oder den Kompaktschlüssel;
       besteht ein Name NUR aus solchen Wörtern, zählen sie doch. */
    if ((REBEN.has(w) || STUFEN.has(w)) && [...m.woerter].some((x) => !REBEN.has(x) && !STUFEN.has(x))) continue;
    for (const e of index.nachWort.get(w) || []) kandidaten.add(e);
  }
  const raus = [];
  for (const e of kandidaten) {
    if (e.o.id === ausserId || (m.id && e.o.id === m.id)) continue;
    const v = vergleiche(index.typ, m, e.m);
    if (v) raus.push({ objekt: e.o, ...v });
  }
  raus.sort((x, y) => (x.stufe === y.stufe ? 0 : x.stufe === "sehr" ? -1 : 1) || y.punkte - x.punkte
    || String(x.objekt.name).localeCompare(String(y.objekt.name), "de"));
  return raus.slice(0, grenze);
}

module.exports = { SCHWELLE, baueIndex, grundform, kompakt, merkmale, namensNaehe, quelleVon, suche, vergleiche, woerter };
